import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import SettlementMetricsCard from './components/SettlementMetricsCard';
import SettlementWaterfallChart from './components/SettlementWaterfallChart';
import SettlementStatusPanel from './components/SettlementStatusPanel';
import SettlementTable from './components/SettlementTable';
import ReconciliationFilters from './components/ReconciliationFilters';
import { settlementsService } from '../../services/supabaseService';

const FinancialReconciliation = () => {
  const [loading, setLoading] = useState(true);
  const [settlements, setSettlements] = useState([]);
  const [waterfallData, setWaterfallData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [metricsData, setMetricsData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: settlementsData } = await settlementsService?.getAll();
        const { data: stats } = await settlementsService?.getStatistics();

        if (settlementsData) {
          // Transform data for table
          const transformedSettlements = settlementsData?.map(s => ({
            id: s?.settlementId,
            merchantName: s?.merchants?.merchantName || 'Unknown',
            merchantId: s?.merchants?.merchantId || '-',
            settlementDate: new Date(s?.settlementDate)?.toLocaleDateString('id-ID'),
            settlementTime: s?.settlementTime + ' WIB',
            grossAmount: `Rp ${parseFloat(s?.grossAmount || 0)?.toLocaleString('id-ID')}`,
            fees: `Rp ${parseFloat(s?.fees || 0)?.toLocaleString('id-ID')}`,
            netAmount: `Rp ${parseFloat(s?.netAmount || 0)?.toLocaleString('id-ID')}`,
            status: s?.status,
            hasDiscrepancy: s?.hasDiscrepancy,
            discrepancyAmount: s?.discrepancyAmount ? `Rp ${parseFloat(s?.discrepancyAmount)?.toLocaleString('id-ID')}` : null
          }));
          setSettlements(transformedSettlements);
        }

        if (stats) {
          // Calculate waterfall data
          const grossAmount = stats?.totalGrossAmount || 0;
          const fees = (stats?.totalGrossAmount - stats?.totalNetAmount) || 0;
          const gatewayFees = fees * 0.6;
          const bankFees = fees * 0.25;
          const adminFees = fees * 0.15;
          const adjustment = stats?.totalDiscrepancy || 0;

          setWaterfallData([
            { name: "Transaksi Bruto", value: grossAmount },
            { name: "Biaya Gateway", value: -gatewayFees },
            { name: "Biaya Bank", value: -bankFees },
            { name: "Biaya Admin", value: -adminFees },
            { name: "Penyesuaian", value: adjustment },
            { name: "Settlement Neto", value: stats?.totalNetAmount }
          ]);

          // Calculate status data
          const totalSettlements = stats?.totalSettlements || 1;
          const completedCount = stats?.completedSettlements || 0;
          const pendingCount = stats?.pendingSettlements || 0;
          const failedCount = totalSettlements - completedCount - pendingCount;

          setStatusData([
            {
              status: "completed",
              label: "Selesai",
              count: completedCount,
              percentage: Math.round((completedCount / totalSettlements) * 100),
              avgTime: "Rata-rata: 2.5 jam"
            },
            {
              status: "pending",
              label: "Pending",
              count: pendingCount,
              percentage: Math.round((pendingCount / totalSettlements) * 100),
              avgTime: "Rata-rata: 4.8 jam"
            },
            {
              status: "failed",
              label: "Gagal",
              count: failedCount,
              percentage: Math.round((failedCount / totalSettlements) * 100),
              avgTime: "Rata-rata: 6.2 jam"
            }
          ]);

          // Set metrics
          setMetricsData({
            totalSettlement: {
              title: "Total Settlement",
              value: `Rp ${(stats?.totalNetAmount / 1000000)?.toFixed(1)} M`,
              subtitle: "Hari ini",
              trend: "up",
              trendValue: "+12.5%",
              icon: "Wallet",
              iconBg: "bg-primary/10",
              iconColor: "var(--color-primary)"
            },
            pendingSettlement: {
              title: "Settlement Pending",
              value: `${pendingCount}`,
              subtitle: "Menunggu proses",
              trend: "down",
              trendValue: "-3.2%",
              icon: "Clock",
              iconBg: "bg-warning/10",
              iconColor: "var(--color-warning)"
            },
            accuracyRate: {
              title: "Tingkat Akurasi",
              value: `${(((totalSettlements - stats?.discrepancyCount) / totalSettlements) * 100)?.toFixed(1)}%`,
              subtitle: "Rekonsiliasi otomatis",
              trend: "up",
              trendValue: "+0.8%",
              icon: "Target",
              iconBg: "bg-success/10",
              iconColor: "var(--color-success)"
            },
            discrepancies: {
              title: "Perbedaan Outstanding",
              value: `Rp ${(stats?.totalDiscrepancy / 1000000)?.toFixed(1)} M`,
              subtitle: "Perlu review manual",
              trend: "down",
              trendValue: "-18.4%",
              icon: "AlertTriangle",
              iconBg: "bg-error/10",
              iconColor: "var(--color-error)"
            }
          });
        }
      } catch (error) {
        console.error('Error fetching settlement data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Real-time subscription
  useEffect(() => {
    const unsubscribe = settlementsService?.subscribe((payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        const newSettlement = {
          id: payload?.new?.settlementId,
          merchantName: payload?.new?.merchants?.merchantName || 'Unknown',
          merchantId: payload?.new?.merchants?.merchantId || '-',
          settlementDate: new Date(payload.new?.settlementDate)?.toLocaleDateString('id-ID'),
          settlementTime: payload?.new?.settlementTime + ' WIB',
          grossAmount: `Rp ${parseFloat(payload?.new?.grossAmount || 0)?.toLocaleString('id-ID')}`,
          fees: `Rp ${parseFloat(payload?.new?.fees || 0)?.toLocaleString('id-ID')}`,
          netAmount: `Rp ${parseFloat(payload?.new?.netAmount || 0)?.toLocaleString('id-ID')}`,
          status: payload?.new?.status,
          hasDiscrepancy: payload?.new?.hasDiscrepancy,
          discrepancyAmount: payload?.new?.discrepancyAmount ? `Rp ${parseFloat(payload?.new?.discrepancyAmount)?.toLocaleString('id-ID')}` : null
        };
        setSettlements(prev => [newSettlement, ...prev]);
      } else if (payload?.eventType === 'UPDATE' && payload?.new) {
        setSettlements(prev => prev?.map(s => 
          s?.id === payload?.new?.settlementId ? {
            ...s,
            status: payload?.new?.status,
            hasDiscrepancy: payload?.new?.hasDiscrepancy
          } : s
        ));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleSort = (key, direction) => {
    const sortedSettlements = [...settlements]?.sort((a, b) => {
      let aValue = a?.[key];
      let bValue = b?.[key];

      if (key === 'grossAmount' || key === 'fees' || key === 'netAmount') {
        aValue = parseInt(aValue?.replace(/[^0-9]/g, ''));
        bValue = parseInt(bValue?.replace(/[^0-9]/g, ''));
      }

      if (direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    setSettlements(sortedSettlements);
  };

  const handleBulkAction = (action, selectedIds) => {
    console.log(`Bulk action: ${action}`, selectedIds);
  };

  const handleFilterChange = (filters) => {
    console.log('Filters changed:', filters);
  };

  const handleExport = () => {
    console.log('Exporting reconciliation report...');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="content-container">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Rekonsiliasi Keuangan
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Pantau settlement, lacak rekonsiliasi, dan analisis aliran keuangan secara real-time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <SettlementMetricsCard {...metricsData?.totalSettlement} />
            <SettlementMetricsCard {...metricsData?.pendingSettlement} />
            <SettlementMetricsCard {...metricsData?.accuracyRate} />
            <SettlementMetricsCard {...metricsData?.discrepancies} />
          </div>

          <ReconciliationFilters 
            onFilterChange={handleFilterChange}
            onExport={handleExport}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-9">
              <SettlementWaterfallChart data={waterfallData} />
            </div>
            <div className="lg:col-span-3">
              <SettlementStatusPanel statusData={statusData} />
            </div>
          </div>

          <SettlementTable 
            settlements={settlements}
            onSort={handleSort}
            onBulkAction={handleBulkAction}
          />
        </div>
      </main>
    </div>
  );
};

export default FinancialReconciliation;